import { useCallback, useSyncExternalStore } from 'react';
import { create, get, update } from './repo';
import { useEntity } from './store';

// Two kinds of settings:
// - synced settings: encrypted entities (kind 'setting', id = key), same on every device
// - device prefs: localStorage, this device only (theme, sidebar width, cache size…)

export function getSetting<T>(key: string, fallback: T): T {
  const s = get('setting', key);
  return s && !s.deleted ? (s.value as T) : fallback;
}

export function setSetting(key: string, value: unknown) {
  if (get('setting', key)) update('setting', key, { value, deleted: false });
  else create('setting', { id: key, value });
}

export function useSetting<T>(key: string, fallback: T): [T, (v: T) => void] {
  const s = useEntity('setting', key);
  const value = s && !s.deleted ? (s.value as T) : fallback;
  const setter = useCallback((v: T) => setSetting(key, v), [key]);
  return [value, setter];
}

// ------------------------------------------------------------------ device prefs

const PREFIX = 'ember.';
const prefListeners = new Set<() => void>();
const prefCache = new Map<string, unknown>();

export function getPref<T>(key: string, fallback: T): T {
  if (prefCache.has(key)) return prefCache.get(key) as T;
  let v: T = fallback;
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (raw !== null) v = JSON.parse(raw) as T;
  } catch {}
  prefCache.set(key, v);
  return v;
}

export function setPref(key: string, value: unknown) {
  prefCache.set(key, value);
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {}
  for (const l of prefListeners) l();
}

export function usePref<T>(key: string, fallback: T): [T, (v: T) => void] {
  const value = useSyncExternalStore(
    (cb) => {
      prefListeners.add(cb);
      return () => prefListeners.delete(cb);
    },
    () => getPref(key, fallback),
  );
  const setter = useCallback((v: T) => setPref(key, v), [key]);
  return [value, setter];
}

// ------------------------------------------------------------------ theme

export type ThemePref = 'dark' | 'light' | 'system';

/** The app has one theme (dark); the old light/system choice is ignored. */
export function applyTheme(_pref: ThemePref = 'dark') {
  document.documentElement.dataset.theme = 'dark';
  try {
    localStorage.removeItem('ember.theme');
  } catch {}
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#0e0f11');
}

export function setTheme(pref: ThemePref) {
  prefCache.set('theme', pref);
  applyTheme(pref);
  for (const l of prefListeners) l();
}

export function useTheme(): [ThemePref, (t: ThemePref) => void] {
  const value = useSyncExternalStore(
    (cb) => {
      prefListeners.add(cb);
      return () => prefListeners.delete(cb);
    },
    () => {
      try {
        return (localStorage.getItem('ember.theme') as ThemePref | null) ?? 'dark';
      } catch {
        return 'dark';
      }
    },
  );
  return [value, setTheme];
}
